<!-- src/components/CaseWizardModal.vue -->
<!--
  Case creation wizard. Replaces NewCaseModal.

  Five steps:
    1. Court        — single free-text field ("King County Superior")
    2. Documents    — multi-select from the agency's library
    3. People       — defendant + 0-2 indemnitors
    4. Details      — agent-audience required fields derived from
                      the documents chosen in step 2
    5. Invite       — STUB. Confirm-and-send UI to be designed.

  Dead-simple ethos: nothing is required. An empty wizard creates
  an empty draft. The wizard is a guided structure, not a
  validation gauntlet — agents take 2am calls and capture what
  they know.

  v-model controls open/closed.
  Emits 'create' with the new case object when the agent confirms.
-->

<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="780"
    :fullscreen="$vuetify.display.smAndDown"
    scrollable
  >
    <v-card class="wizard-card">

      <!-- ─── Header ──────────────────────────────────────── -->
      <v-card-title class="wizard-title">
        {{ modalTitle }}
        <v-spacer />
        <v-btn
          icon
          variant="text"
          size="small"
          @click="onCancel"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="wizard-subtitle">
        Fill in what you know. You can always come back later.
      </v-card-subtitle>

      <v-divider />

      <!-- ─── Stepper ─────────────────────────────────────── -->
      <!-- Stepper lives inside v-card-text so the dialog's
           `scrollable` prop applies. The stepper header is made
           sticky in CSS so it stays visible while the body
           scrolls (important on step 3 once an indemnitor is
           added and content runs long). -->
      <v-card-text class="wizard-content">
        <v-stepper
          v-model="currentStep"
          flat
          hide-actions
          alt-labels
          bg-color="transparent"
          class="wizard-stepper"
        >
        <v-stepper-header class="wizard-stepper-header">
          <template
            v-for="(step, idx) in steps"
            :key="step.key"
          >
            <v-stepper-item
              :value="idx + 1"
              :title="step.title"
              :complete="currentStep > idx + 1"
              complete-icon="mdi-check"
              editable
              color="#635347"
            />
            <v-divider v-if="idx < steps.length - 1" />
          </template>
        </v-stepper-header>

        <v-stepper-window class="wizard-window">

          <!-- ─── STEP 1: CASE ─────────────────────────────── -->
          <v-stepper-window-item :value="1">
            <div class="step-body">
              <div class="step-heading">About the case.</div>
              <div class="step-hint">
                The case number from the court, and where the case will be
                heard. Both can be filled in later if you don't have them
                yet.
              </div>

              <v-text-field
                :model-value="form.caseNumber"
                @update:model-value="(v) => form.caseNumber = sanitizeCaseId(v)"
                label="Case number"
                placeholder="456789"
                hint="From the court"
                persistent-hint
                variant="outlined"
                density="comfortable"
                autofocus
                class="step1-field"
              />

              <v-text-field
                v-model="form.court"
                label="Court"
                placeholder="King County Superior"
                variant="outlined"
                density="comfortable"
                hide-details
                class="step1-field"
              />
            </div>
          </v-stepper-window-item>

          <!-- ─── STEP 2: DOCUMENTS ────────────────────────── -->
          <v-stepper-window-item :value="2">
            <div class="step-body">
              <div class="step-heading">Which documents does this case need?</div>
              <div class="step-hint">
                Pick everything that applies. You can change this later.
              </div>

              <div v-if="library.length === 0" class="empty-library">
                <v-icon size="36" color="grey-lighten-1">mdi-file-document-outline</v-icon>
                <div class="empty-library-text">
                  No documents are set up for this agency yet.
                </div>
                <div class="empty-library-hint">
                  An administrator can add documents in the authoring tool.
                  You can still create the case and add documents to it later.
                </div>
              </div>

              <div v-else class="document-list">
                <div
                  v-for="doc in library"
                  :key="doc.document_slug"
                  :class="[
                    'document-card',
                    { 'document-card-selected': isDocumentSelected(doc) },
                  ]"
                  @click="toggleDocument(doc)"
                >
                  <v-checkbox
                    :model-value="isDocumentSelected(doc)"
                    color="#635347"
                    density="compact"
                    hide-details
                    class="document-checkbox"
                    @click.stop
                    @update:model-value="toggleDocument(doc)"
                  />
                  <div class="document-body">
                    <div class="document-title">{{ doc.display_name }}</div>
                    <div class="document-summary">{{ doc.summary }}</div>
                    <div class="document-meta">
                      <v-icon
                        size="14"
                        :class="signerIconClass(doc.signer_role)"
                      >
                        {{ signerIcon(doc.signer_role) }}
                      </v-icon>
                      <span>Signed by {{ signerLabel(doc.signer_role) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-stepper-window-item>

          <!-- ─── STEP 3: PEOPLE ───────────────────────────── -->
          <v-stepper-window-item :value="3">
            <div class="step-body">
              <div class="step-heading">Who's on the case?</div>
              <div class="step-hint">
                One defendant. Up to two indemnitors. Any field can be left
                blank if you don't know it yet.
              </div>

              <!-- Defendant -->
              <div class="person-block">
                <div class="person-header">
                  <v-icon size="18" class="person-icon person-icon-defendant">
                    mdi-account-circle
                  </v-icon>
                  <span class="person-title">Defendant</span>
                </div>
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      v-model="form.defendant.firstName"
                      label="First name"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="form.defendant.lastName"
                      label="Last name"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      :model-value="form.defendant.phone"
                      @update:model-value="(v) => form.defendant.phone = fmtPhone(v)"
                      label="Phone"
                      variant="outlined"
                      density="comfortable"
                      inputmode="tel"
                      :error-messages="fieldErrors['defendant.phone']"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="form.defendant.email"
                      label="Email"
                      type="email"
                      variant="outlined"
                      density="comfortable"
                      :error-messages="fieldErrors['defendant.email']"
                      hide-details="auto"
                    />
                  </v-col>
                </v-row>
              </div>

              <!-- Indemnitor block(s) -->
              <div
                v-for="(indem, idx) in form.indemnitors"
                :key="idx"
                class="person-block"
              >
                <div class="person-header">
                  <v-icon size="18" class="person-icon person-icon-indemnitor">
                    mdi-shield-account
                  </v-icon>
                  <span class="person-title">
                    {{ idx === 0 ? 'Indemnitor' : 'Second Indemnitor' }}
                  </span>
                  <v-spacer />
                  <v-btn
                    v-if="idx === 1"
                    size="x-small"
                    variant="text"
                    color="grey-darken-1"
                    @click="removeIndemnitor(idx)"
                  >
                    <v-icon start size="14">mdi-close</v-icon>
                    Remove
                  </v-btn>
                </div>
                <v-row dense>
                  <v-col cols="6">
                    <v-text-field
                      v-model="indem.firstName"
                      label="First name"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="indem.lastName"
                      label="Last name"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      :model-value="indem.phone"
                      @update:model-value="(v) => indem.phone = fmtPhone(v)"
                      label="Phone"
                      variant="outlined"
                      density="comfortable"
                      inputmode="tel"
                      :error-messages="fieldErrors[`indem${idx}.phone`]"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="indem.email"
                      label="Email"
                      type="email"
                      variant="outlined"
                      density="comfortable"
                      :error-messages="fieldErrors[`indem${idx}.email`]"
                      hide-details="auto"
                    />
                  </v-col>
                </v-row>
              </div>

              <!-- Add second indemnitor -->
              <v-btn
                v-if="form.indemnitors.length === 1"
                variant="text"
                color="#635347"
                @click="addIndemnitor"
                class="add-indemnitor-btn"
              >
                <v-icon start>mdi-plus</v-icon>
                Add second indemnitor
              </v-btn>
            </div>
          </v-stepper-window-item>

          <!-- ─── STEP 4: DETAILS ──────────────────────────── -->
          <v-stepper-window-item :value="4">
            <div class="step-body">
              <div class="step-heading">A few case details.</div>
              <div class="step-hint">
                These are the things the chosen documents need filled in by
                the agent. Fields marked with * are required.
              </div>

              <!-- Loading -->
              <div v-if="agentFieldsLoading" class="empty-fields">
                <v-progress-circular indeterminate size="20" width="2" color="#635347" />
                <span>Loading fields…</span>
              </div>

              <!-- Empty -->
              <div
                v-else-if="agentFields.length === 0"
                class="empty-fields"
              >
                <v-icon size="32" color="grey-lighten-1">mdi-information-outline</v-icon>
                <div class="empty-fields-text">
                  <template v-if="form.selectedDocSlugs.length === 0">
                    No documents are selected yet, so there's nothing extra
                    to fill in. You can go back and pick documents, or skip
                    this step.
                  </template>
                  <template v-else>
                    The chosen documents don't need any case-level fields
                    from the agent.
                  </template>
                </div>
              </div>

              <!-- Agent fields (flat list from server) -->
              <div v-else class="agent-fields-list">
                <div
                  v-for="field in agentFields"
                  :key="field.field_id"
                  class="field-row"
                >
                  <!-- text -->
                  <v-text-field
                    v-if="field.type === 'text'"
                    v-model="form.caseFields[field.field_id]"
                    :label="field.display_label + (field.required ? ' *' : '')"
                    :placeholder="field.placeholder"
                    :hint="field.why_we_need_this"
                    :persistent-hint="!!field.why_we_need_this"
                    :error-messages="fieldErrors[field.field_id]"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                  />

                  <!-- text_multiline -->
                  <v-textarea
                    v-else-if="field.type === 'text_multiline'"
                    v-model="form.caseFields[field.field_id]"
                    :label="field.display_label + (field.required ? ' *' : '')"
                    :placeholder="field.placeholder"
                    :hint="field.why_we_need_this"
                    :persistent-hint="!!field.why_we_need_this"
                    :error-messages="fieldErrors[field.field_id]"
                    variant="outlined"
                    density="comfortable"
                    rows="2"
                    auto-grow
                    hide-details="auto"
                  />

                  <!-- currency -->
                  <v-text-field
                    v-else-if="field.type === 'currency'"
                    :model-value="form.caseFields[field.field_id]"
                    @update:model-value="(v) => form.caseFields[field.field_id] = sanitizeCurrency(v)"
                    :label="field.display_label + (field.required ? ' *' : '')"
                    :placeholder="field.placeholder"
                    :hint="field.why_we_need_this"
                    :persistent-hint="!!field.why_we_need_this"
                    :error-messages="fieldErrors[field.field_id]"
                    prefix="$"
                    variant="outlined"
                    density="comfortable"
                    inputmode="numeric"
                    hide-details="auto"
                  />

                  <!-- date -->
                  <v-text-field
                    v-else-if="field.type === 'date'"
                    v-model="form.caseFields[field.field_id]"
                    :label="field.display_label + (field.required ? ' *' : '')"
                    :placeholder="field.placeholder || 'MM/DD/YYYY'"
                    :error-messages="fieldErrors[field.field_id]"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                  />

                  <!-- fallback for unsupported types -->
                  <v-text-field
                    v-else
                    v-model="form.caseFields[field.field_id]"
                    :label="field.display_label + (field.required ? ' *' : '')"
                    :error-messages="fieldErrors[field.field_id]"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                  />
                </div>
              </div>
            </div>
          </v-stepper-window-item>

        </v-stepper-window>
      </v-stepper>
      </v-card-text>

      <v-divider />

      <!-- ─── Footer actions ──────────────────────────────── -->
      <v-card-actions class="wizard-actions">
        <v-btn
          v-if="currentStep > 1"
          variant="text"
          @click="goBack"
        >
          <v-icon start>mdi-chevron-left</v-icon>
          Back
        </v-btn>
        <v-spacer />
        <v-btn
          variant="text"
          @click="onCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          v-if="currentStep < steps.length"
          color="#635347"
          variant="flat"
          @click="goNext"
        >
          Next
          <v-icon end>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          color="#635347"
          variant="flat"
          @click="onConfirm"
        >
          {{ confirmButtonLabel }}
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>


<script>
import { wizardData } from '../data/wizardMockData.js';
import { formatPhone, phoneToE164 } from '../utils/phone.js';
import { auth } from '../firebase';

// ─── Shape helpers ────────────────────────────────────────────

// Fresh empty form. Called when the modal opens so each case
// starts clean.
function emptyForm() {
  return {
    caseNumber: '',
    court: '',
    selectedDocSlugs: [],
    defendant: { firstName: '', lastName: '', phone: '', email: '' },
    indemnitors: [{ firstName: '', lastName: '', phone: '', email: '' }],
    caseFields: {},   // { [field_id]: value }
  };
}

// Step metadata. Keep titles short — they have to fit in the
// stepper header even on narrow screens.
const STEPS = [
  { key: 'case',      title: 'Case' },
  { key: 'documents', title: 'Documents' },
  { key: 'people',    title: 'People' },
  { key: 'details',   title: 'Details' },
];

// Build the wizard's form shape from an existing case object —
// the reverse of buildCaseObject. Reads the API shape
// (participants.defendant.identity, case_fields.court, etc.)
// and transforms back into the wizard's internal form fields.
// Stored phones are in E.164 (+1...); the wizard's UI shows
// them format-as-you-type, so they run through formatPhone()
// on the way in. Returns the same shape as emptyForm().
function formFromCase(c) {
  // case_fields minus the two step-1 fields (court, court_case_number)
  // — those populate the dedicated step-1 inputs, not the step-4
  // dynamic field grid.
  const caseFields = { ...(c.case_fields || {}) };
  delete caseFields.court;
  delete caseFields.court_case_number;

  const def = c.participants?.defendant;
  const ind1 = c.participants?.indemnitor1;
  const ind2 = c.participants?.indemnitor2;

  const indemnitors = [ind1, ind2]
    .filter(Boolean)
    .map(p => ({
      firstName: p.identity?.first_name || '',
      lastName:  p.identity?.last_name  || '',
      phone:     formatPhone(p.identity?.phone || ''),
      email:     p.identity?.email      || '',
    }));
  if (indemnitors.length === 0) {
    indemnitors.push({ firstName: '', lastName: '', phone: '', email: '' });
  }

  return {
    caseNumber: c.case_fields?.court_case_number || '',
    court:      c.case_fields?.court             || '',
    selectedDocSlugs: (c.selected_documents || []).map(d => d.slug),
    defendant: {
      firstName: def?.identity?.first_name || '',
      lastName:  def?.identity?.last_name  || '',
      phone:     formatPhone(def?.identity?.phone || ''),
      email:     def?.identity?.email      || '',
    },
    indemnitors,
    caseFields,
  };
}

// Pretty signer-role labels for the document cards.
const SIGNER_LABELS = {
  defendant:  'the defendant',
  indemnitor: 'an indemnitor',
};

export default {
  name: 'CaseWizardModal',

  props: {
    modelValue: { type: Boolean, default: false },
    // companyNumber of the agency the wizard is creating a case
    // for. Defaults to A-Affordable for the dev gate.
    companyNumber: { type: Number, default: 1008 },
    // When non-null, the wizard opens in edit mode: pre-populates
    // the form from this case, swaps the title to "Edit Case" and
    // the step-4 button to "Save case", and emits 'save' instead
    // of 'create' on confirm. The emitted object is a *partial* —
    // only the fields the wizard manages. The parent merges it
    // onto the existing case so untouched fields (notes, message
    // threads, consent state, etc.) are preserved.
    editCase: { type: Object, default: null },
    // Step to land on when the modal opens. Defaults to 1 for new
    // cases. Forms.vue passes 3 when the agent clicks a
    // participant name/phone/email to edit.
    initialStep: { type: Number, default: 1 },
  },

  emits: ['update:modelValue', 'create', 'save'],

  data() {
    return {
      currentStep: 1,
      steps: STEPS,
      form: emptyForm(),

      // Per-field transient error messages. Keyed by a dotted
      // path: 'defendant.phone', 'defendant.email', 'indem0.phone',
      // 'indem0.email', 'indem1.phone', 'indem1.email'. When a key
      // has messages, Vuetify renders the field in its standard
      // red error state. flashFieldErrors() populates this on a
      // failed Next click and then a setTimeout wipes it back to
      // empty after the flash duration.
      fieldErrors: {},

      // Handle for the pending flash-clear timeout, so back-to-back
      // Next clicks don't pile up timers.
      flashTimer: null,

      // Agent fields fetched from the server for step 4. Populated
      // by fetchAgentFields() whenever selectedDocSlugs changes.
      agentFields: [],
      agentFieldsLoading: false,
    };
  },

  computed: {
    // The agency record for the current companyNumber, or null.
    agency() {
      return wizardData.getAgency(this.companyNumber);
    },

    // The agency's document library. Empty array if the agency
    // has no documents set up yet (e.g. PNWBB / 1002 today).
    library() {
      if (!this.agency) return [];
      return wizardData.getDocumentsForAgency(this.agency.agency_id);
    },

    // ── Edit-mode plumbing ───────────────────────────────────
    isEditing() {
      return !!this.editCase;
    },
    modalTitle() {
      return this.isEditing ? 'Edit Case' : 'New Case';
    },
    confirmButtonLabel() {
      return this.isEditing ? 'Save case' : 'Create case';
    },
  },

  watch: {
    // Reset every time the modal opens. A previous session's data
    // never leaks into the next case.
    modelValue(isOpen) {
      if (isOpen) {
        // In edit mode, populate the form from the supplied case
        // and land on the supplied initialStep (the dashboard
        // passes 3 — directly to the People step). In create
        // mode, blank form, step 1.
        if (this.editCase) {
          this.form = formFromCase(this.editCase);
          this.currentStep = this.initialStep || 1;
        } else {
          this.form = emptyForm();
          this.currentStep = this.initialStep || 1;
        }
        this.fieldErrors = {};
        if (this.flashTimer) {
          clearTimeout(this.flashTimer);
          this.flashTimer = null;
        }
        // Fetch agent fields if documents are already selected
        // (edit mode with pre-selected docs).
        this.fetchAgentFields();
      }
    },

    // Re-fetch agent fields whenever document selection changes.
    'form.selectedDocSlugs': {
      deep: true,
      handler() {
        this.fetchAgentFields();
      },
    },
  },

  methods: {
    // ── Phone formatting ─────────────────────────────────────
    fmtPhone(raw) {
      return formatPhone(raw);
    },

    // ── Currency sanitizer ───────────────────────────────────
    // Lifted from NewCaseModal's sanitizeBond. Keeps digits and
    // at most one decimal point.
    sanitizeCurrency(raw) {
      if (!raw) return '';
      const cleaned = String(raw).replace(/[^0-9.]/g, '');
      const parts = cleaned.split('.');
      if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('');
      return cleaned;
    },

    // ── Case-number sanitizer ────────────────────────────────
    // Case IDs vary by court / agency. We strip whitespace and
    // most punctuation but otherwise accept alphanumerics,
    // hyphens, underscores, and forward slashes. Preserves
    // leading zeros.
    sanitizeCaseId(raw) {
      if (!raw) return '';
      return String(raw).replace(/[^A-Za-z0-9_/-]/g, '');
    },

    // ── Agent fields (step 4) ────────────────────────────────
    // Fetches the merged agent fields for the selected documents
    // from the server. The endpoint resolves each document's
    // agent_fields list through its bindings and the field
    // collection, returning type/label/placeholder/required.
    async fetchAgentFields() {
      const slugs = this.form.selectedDocSlugs;
      if (!slugs || slugs.length === 0) {
        this.agentFields = [];
        return;
      }
      this.agentFieldsLoading = true;
      try {
        const user = auth.currentUser
          || await new Promise((resolve) => {
            const unsub = auth.onAuthStateChanged((u) => {
              unsub();
              resolve(u);
            });
          });
        if (!user) return;
        const token = await user.getIdToken();
        const qs = `slugs=${encodeURIComponent(slugs.join(','))}`;
        const resp = await fetch(
          `/portaldb/db/bbas/documents/agent-fields?${qs}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await resp.json();
        if (data && data.success) {
          this.agentFields = data.fields || [];
        }
      } catch (err) {
        console.error('[CaseWizard] fetchAgentFields error:', err);
      } finally {
        this.agentFieldsLoading = false;
      }
    },

    // ── Document selection ───────────────────────────────────
    isDocumentSelected(doc) {
      return this.form.selectedDocSlugs.includes(doc.document_slug);
    },

    toggleDocument(doc) {
      const idx = this.form.selectedDocSlugs.indexOf(doc.document_slug);
      if (idx === -1) {
        this.form.selectedDocSlugs.push(doc.document_slug);
      } else {
        this.form.selectedDocSlugs.splice(idx, 1);
      }
    },

    signerIcon(role) {
      return role === 'defendant' ? 'mdi-account-circle' : 'mdi-shield-account';
    },
    signerIconClass(role) {
      return role === 'defendant'
        ? 'signer-icon-defendant'
        : 'signer-icon-indemnitor';
    },
    signerLabel(role) {
      return SIGNER_LABELS[role] || role;
    },

    // ── Indemnitor management ────────────────────────────────
    addIndemnitor() {
      if (this.form.indemnitors.length >= 2) return;
      this.form.indemnitors.push({
        firstName: '', lastName: '', phone: '', email: '',
      });
    },

    removeIndemnitor(idx) {
      this.form.indemnitors.splice(idx, 1);
    },

    // ── Validation predicates ────────────────────────────────
    // Both return true for empty values — the fields are
    // optional, only typed values get shape-checked.
    isValidEmail(v) {
      if (!v) return true;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    },
    isValidPhone(v) {
      if (!v) return true;
      // International numbers (not +1) pass through unchecked —
      // we don't try to validate every country's format.
      if (v.startsWith('+') && !v.startsWith('+1')) return true;
      // US: must be exactly 10 digits after stripping format chars.
      const digits = v.replace(/\D/g, '');
      return digits.length === 10;
    },

    // ── Flash-red validation ────────────────────────────────
    // Validates step 3's phone and email fields. For each invalid
    // value, sets an error message on the matching path in
    // fieldErrors — Vuetify renders the field red. After the
    // flash duration, the errors are cleared and the fields
    // return to normal. Returns true if everything was valid
    // (caller may proceed), false otherwise (caller stays put).
    flashFieldErrors() {
      const errs = {};

      // Defendant
      if (!this.isValidPhone(this.form.defendant.phone)) {
        errs['defendant.phone'] = 'Enter a valid phone number';
      }
      if (!this.isValidEmail(this.form.defendant.email)) {
        errs['defendant.email'] = 'Enter a valid email address';
      }

      // Indemnitors
      this.form.indemnitors.forEach((indem, idx) => {
        if (!this.isValidPhone(indem.phone)) {
          errs[`indem${idx}.phone`] = 'Enter a valid phone number';
        }
        if (!this.isValidEmail(indem.email)) {
          errs[`indem${idx}.email`] = 'Enter a valid email address';
        }
      });

      if (Object.keys(errs).length === 0) return true;

      // Cancel any earlier flash that's still in flight so the
      // new one gets its full duration.
      if (this.flashTimer) clearTimeout(this.flashTimer);
      this.fieldErrors = errs;
      this.flashTimer = setTimeout(() => {
        this.fieldErrors = {};
        this.flashTimer = null;
      }, 2000);
      return false;
    },

    // ── Step navigation ──────────────────────────────────────
    goBack() {
      if (this.currentStep > 1) this.currentStep -= 1;
    },

    goNext() {
      // Email + phone validation gate on step 3. If anything is
      // malformed, flash the offending fields red and stay on
      // step 3. The red fades after ~2s and the agent can either
      // fix the fields in place or just try Next again to see the
      // flash again.
      if (this.currentStep === 3) {
        if (!this.flashFieldErrors()) return;
      }
      if (this.currentStep < this.steps.length) this.currentStep += 1;
    },

    // ── Actions ──────────────────────────────────────────────
    onCancel() {
      this.$emit('update:modelValue', false);
    },

    onConfirm() {
      // Validate required agent fields before creating/saving.
      const errs = {};
      for (const field of this.agentFields) {
        if (field.required) {
          const val = this.form.caseFields[field.field_id];
          if (!val || (typeof val === 'string' && !val.trim())) {
            errs[field.field_id] = 'Required';
          }
        }
      }
      if (Object.keys(errs).length > 0) {
        if (this.flashTimer) clearTimeout(this.flashTimer);
        this.fieldErrors = errs;
        this.flashTimer = setTimeout(() => {
          this.fieldErrors = {};
          this.flashTimer = null;
        }, 2000);
        return;
      }

      const obj = this.buildCaseObject();
      this.$emit(this.isEditing ? 'save' : 'create', obj);
      this.$emit('update:modelValue', false);
    },

    // Convert the wizard's form into a case payload for the parent.
    //
    // In create mode: an API-shaped object matching what POST
    // /portaldb/db/bbas/cases expects. The server stamps _id,
    // timestamps, agency_id, surety_affiliation, per-participant
    // defaults, etc.
    //
    // In edit mode: an API-shaped partial matching what PATCH
    // /portaldb/db/bbas/cases/:id expects. Includes _id so the
    // parent knows which case to PATCH. Only mutable fields are
    // sent; the server ignores anything outside the allowlist.
    buildCaseObject() {
      const cleanIdentity = (p) => ({
        first_name: p.firstName.trim(),
        last_name:  p.lastName.trim(),
        phone:      phoneToE164(p.phone),
        email:      p.email.trim() || null,
      });

      // case_fields: merge step-1 values (court, court_case_number)
      // with step-4 dynamic agent fields (bond_amount_numeric, etc.)
      const case_fields = {
        court: this.form.court.trim() || null,
        court_case_number: this.form.caseNumber.trim() || null,
      };
      for (const [key, val] of Object.entries(this.form.caseFields)) {
        if (val !== '' && val !== null && val !== undefined) {
          case_fields[key] = val;
        }
      }

      // Participants: defendant is always present. Indemnitors only
      // if the agent filled at least one identity field.
      const participants = {
        defendant: { identity: cleanIdentity(this.form.defendant) },
      };

      // Filter filled indemnitors for create; for edit, also handle
      // removal (agent cleared an existing indemnitor → send null).
      const filledIndemnitors = this.form.indemnitors
        .filter(i => i.firstName || i.lastName || i.phone || i.email);

      if (this.isEditing) {
        // For each potential indemnitor slot, determine whether the
        // edit represents an addition, an update, or a removal.
        for (let i = 0; i < 2; i++) {
          const role = i === 0 ? 'indemnitor1' : 'indemnitor2';
          const formData = this.form.indemnitors[i];
          const existed = !!this.editCase?.participants?.[role];
          const hasFill = formData
            && (formData.firstName || formData.lastName
                || formData.phone || formData.email);
          if (hasFill) {
            participants[role] = { identity: cleanIdentity(formData) };
          } else if (existed) {
            participants[role] = null; // removal signal → PATCH $unsets
          }
        }
      } else {
        if (filledIndemnitors[0]) {
          participants.indemnitor1 = { identity: cleanIdentity(filledIndemnitors[0]) };
        }
        if (filledIndemnitors[1]) {
          participants.indemnitor2 = { identity: cleanIdentity(filledIndemnitors[1]) };
        }
      }

      // Documents: just slug + version. Server stamps added_at/by.
      const selected_documents = this.form.selectedDocSlugs.map(slug => {
        const doc = this.library.find(d => d.document_slug === slug);
        return { slug, version: doc ? doc.template_version : 1 };
      });

      const payload = {
        case_fields,
        participants,
        selected_documents,
      };

      // Create mode: include companyNumber so the server knows
      // which agency to stamp. Edit mode: include _id so the
      // parent knows which case to PATCH.
      if (this.isEditing) {
        payload._id = this.editCase._id;
      } else {
        payload.companyNumber = this.companyNumber;
      }

      return payload;
    },
  },
};
</script>


<style scoped>
/* ─── Card chrome ─────────────────────────────────────────── */
.wizard-card {
  background: #fff;
}
.wizard-title {
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  font-weight: 500;
  color: #635347;
  padding: 16px 20px 8px 20px;
}
.wizard-subtitle {
  color: #888;
  font-size: 0.875rem;
  padding: 0 20px 12px 20px;
}
.wizard-actions {
  padding: 12px 20px;
}

/* ─── Stepper ─────────────────────────────────────────────── */
.wizard-content {
  /* Override Vuetify's default v-card-text padding so the
     stepper sits edge-to-edge inside the dialog. */
  padding: 0 !important;
}
.wizard-stepper {
  box-shadow: none !important;
}
.wizard-stepper-header {
  box-shadow: none !important;
  background: #faf9f6;
  border-bottom: 1px solid #ece8e0;
  padding: 4px 8px;
  /* Stay visible while the body scrolls. */
  position: sticky;
  top: 0;
  z-index: 2;
}
.wizard-window {
  padding: 0;
  background: #fff;
}

/* ─── Step body ───────────────────────────────────────────── */
.step-body {
  padding: 20px 24px 24px 24px;
}
.step-heading {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}
.step-hint {
  font-size: 0.875rem;
  color: #777;
  margin-bottom: 18px;
  line-height: 1.4;
}
.step-hint em {
  font-style: italic;
  color: #555;
}

/* ─── Step 1: case-number + court fields ──────────────────── */
.step1-field {
  max-width: 480px;
}
.step1-field + .step1-field {
  margin-top: 16px;
}

/* ─── Step 2: document cards ──────────────────────────────── */
.document-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.document-card {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 14px 16px 14px 10px;
  background: #faf9f6;
  border: 1px solid #e8e4dc;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 120ms, border-color 120ms;
}
.document-card:hover {
  background: #f4f1ea;
}
.document-card-selected {
  background: #f4ede0;
  border-color: #d2b48c;
}
.document-checkbox {
  flex: 0 0 auto;
  margin-top: -4px;
}
.document-body {
  flex: 1;
  min-width: 0;
}
.document-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #333;
  margin-bottom: 4px;
}
.document-summary {
  font-size: 0.8125rem;
  color: #666;
  line-height: 1.45;
  margin-bottom: 8px;
}
.document-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #888;
}
.signer-icon-defendant {
  color: #635347;
}
.signer-icon-indemnitor {
  color: #d2b48c;
}

.empty-library {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 28px 16px;
  background: #fafafa;
  border: 1px dashed #ddd;
  border-radius: 6px;
}
.empty-library-text {
  font-size: 0.9375rem;
  color: #555;
}
.empty-library-hint {
  font-size: 0.8125rem;
  color: #888;
  text-align: center;
  max-width: 420px;
}

/* ─── Step 3: person blocks ───────────────────────────────── */
.person-block {
  margin-top: 12px;
  padding: 12px;
  background: #faf9f6;
  border-radius: 6px;
  border: 1px solid #e8e4dc;
}
.person-block:first-child {
  margin-top: 0;
}
.person-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.person-title {
  font-weight: 500;
  font-size: 14px;
  color: #555;
}
.person-icon-defendant {
  color: #635347;
}
.person-icon-indemnitor {
  color: #d2b48c;
}
.add-indemnitor-btn {
  margin-top: 12px;
  text-transform: none;
  letter-spacing: 0;
}

/* ─── Step 4: agent fields ────────────────────────────────── */
.field-group {
  margin-bottom: 18px;
}
.field-group-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  margin-bottom: 8px;
  border-bottom: 1px solid #ece8e0;
  padding-bottom: 4px;
}
.field-row {
  margin-bottom: 10px;
}
.empty-fields {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  background: #fafafa;
  border: 1px dashed #ddd;
  border-radius: 6px;
}
.empty-fields-text {
  font-size: 0.875rem;
  color: #666;
  text-align: center;
  max-width: 440px;
  line-height: 1.5;
}
</style>
